import React, { forwardRef, ReactNode, useId } from "react";
import { Typography } from "@mui/material";
import { CoreCheckbox, CoreInfoIndicator } from "../../../core";
import { ProctoringPlatforms } from "@verity/proctoring-option/types";
import { OptionBlockPlatformBadge } from "../optionBlock";
import styles from "./index.module.css";

export type CheckBoxMenuSubItemProps = {
	checked: boolean;
	onChange: (checked: boolean) => void;
	label: ReactNode;
	/**If a description is not provided, tooltip will not be shown */
	description?: ReactNode;
	partiallySupportedPlatforms?: Set<keyof ProctoringPlatforms>;
	/**Aligned with the text */
	children?: React.ReactNode;
	disabled?: boolean;
};

export const CheckBoxMenuSubItem = forwardRef<HTMLDivElement, CheckBoxMenuSubItemProps>(function SwitchMenuItem(
	{ checked, onChange, label, description, partiallySupportedPlatforms, children, disabled },
	ref,
) {
	const handleClick = () => {
		const newIsCheckedState = !checked;
		onChange(newIsCheckedState);
	};
	const id = useId();

	const partiallySupportedPlatformsArray = Array.from(partiallySupportedPlatforms ?? []);

	const showBadges = partiallySupportedPlatformsArray.length > 0;

	return (
		<div className={styles.checkboxSubItemWrapper} ref={ref}>
			<CoreCheckbox
				onClick={handleClick}
				checked={checked}
				size="16px"
				disableFocusRipple
				id={id}
				disabled={disabled}
			/>
			<div className={styles.formColWrapper}>
				<div className={styles.textWrapper}>
					<Typography component="label" className={styles.checkboxLabel} htmlFor={id}>
						{label}
						{showBadges && (
							<span className={styles.badgeContainer}>
								{partiallySupportedPlatformsArray.map((platform) => (
									<OptionBlockPlatformBadge key={platform} platform={platform} />
								))}
							</span>
						)}
					</Typography>
					{description ? <CoreInfoIndicator hint={description} size={20} /> : null}
				</div>
				{children}
			</div>
		</div>
	);
});
